import React, { PropsWithChildren, useContext, useMemo } from "react";
import { OverlayTriggerState } from "react-stately";
import { defineMessages, useIntl } from "react-intl";
import { playlistMessages } from "../playlistMessages";
import PlaylistPreview from "../../../Components/PlaylistPreview/PlaylistPreview";
import CondensedStudentsList from "../../../../../../components/CondensedStudentsList/CondensedStudentsList";

import { Modal } from "@evidenceb/athena-common/design-system/Modal";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import { HierarchyPath } from "../../../../../../components/TreeCheckboxGroup";
import { sessionStore } from "../../../../../../contexts/SessionContext";
import { studentHierarchyPathToObject } from "../utils";
import PlaylistBottomSheet from "../../../Components/PlaylistBottomSheet/PlaylistBottomSheet";
import { DraftPlaylist } from "../../../type";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { ResourcePath } from "../../utils";

import "./SendConfirmationModal.scss";
import ButtonWithLoadingState from "../../../../../../components/ButtonWithLoadingState/ButtonWithLoadingState";

type Props = {
    state: OverlayTriggerState;
    playlist: DraftPlaylist;
    resources: ResourcePath[];
    students: HierarchyPath[];
    isConfirmSaving?: boolean;
    onConfirm: () => void;
};

const SendConfirmationModal = ({
    state,
    playlist,
    resources,
    students,
    onConfirm,
    isConfirmSaving,
}: Props) => {
    const intl = useIntl();
    const {
        session: { classrooms },
    } = useContext(sessionStore);

    const studentsData = useMemo(
        () => studentHierarchyPathToObject(students, classrooms),
        [students, classrooms]
    );

    return (
        <Container
            state={state}
            title={intl.formatMessage(messages.sendPlaylist, {
                playlistName: playlist.name,
            })}
        >
            <p className="playlist-send-modal__warning">
                {intl.formatMessage(messages.editionWarning)}
            </p>
            <PlaylistPreview
                className="playlist-send-modal__preview"
                title={playlist.name ?? ""}
                resourcesNb={resources.length}
            />
            <h3 className="playlist-send-modal__recipients-title">
                {intl.formatMessage(playlistMessages.recipients)}
            </h3>
            <CondensedStudentsList
                className="playlist-send-modal__recipients-list"
                students={studentsData}
            />
            <div className="playlist-send-modal__action-tray">
                <Button
                    color="primary"
                    onPress={() => state.close()}
                    label={intl.formatMessage(playlistMessages.cancel)}
                />
                <ButtonWithLoadingState
                    color="secondary"
                    onPress={onConfirm}
                    loading={isConfirmSaving ?? false}
                    label={intl.formatMessage(playlistMessages.send)}
                />
            </div>
        </Container>
    );
};
export default SendConfirmationModal;

type ContainerProps = PropsWithChildren<{
    state: OverlayTriggerState;
    title: string;
}>;
const Container = ({ title, state, children }: ContainerProps) => {
    const { isTabletOrSmaller } = useBreakpoints();

    if (isTabletOrSmaller)
        return (
            <PlaylistBottomSheet state={state} dialogTitle={title}>
                <div className="playlist-send-modal">{children}</div>
            </PlaylistBottomSheet>
        );

    return (
        <Modal
            className={"playlist-send-modal"}
            open={state.isOpen}
            onClose={() => state.close()}
            title={title}
            disableFooter
        >
            <div className="playlist-send-modal">{children}</div>
        </Modal>
    );
};

const messages = defineMessages({
    sendPlaylist: {
        id: "playlist-sendPlaylistName",
        defaultMessage: 'Send "{playlistName}"',
    },
    editionWarning: {
        id: "playlist-editionAfterSendWarning",
        defaultMessage:
            "After sending this playlist, you will not be able to edit it anymore.",
    },
});
